import {
  Box,
  Paper,
  Stack,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { careersContent } from "../../data/careersContent";
import { formatTextWithHighlight } from "../utils/formatTextWithHighlight";
import useResponsiveSpacingGrid from "../../hooks/useResponsiveSpacingGrid";

interface TimelineItem {
  label: string;
  title: string;
  description: string;
}

const CareerPathTimeline: React.FC = () => {
  const theme = useTheme();
  const spacing = useResponsiveSpacingGrid();
  const { title, timeline, closingRemark } = careersContent.careerPath;

  const bulletPoints = [
    "**Daily feedback** and **structured coaching**.",
    "A **clear progression path**, mapped from day one.",
    "A system built for **performance, not politics**.",
  ];

  return (
    <Box
      component="section"
      sx={{
        padding: theme.spacing(4, 0),
        justifyContent: "center",
        alignItems: "center",
        mt: { sm: 8, md: 8, lg: 8 },
      }}
    >
      {/* Header */}
      <Typography
        variant="h3"
        align="left"
        gutterBottom
        sx={{ fontWeight: 700, mb: { xs: 2, md: 3 } }}
      >
        {title}
      </Typography>

      {/* Lista con puntos destacados */}
      <Box
        sx={{
          display: "flex",
          mb: 3,
        }}
      >
        <List sx={{ maxWidth: "970px", width: "100%" }}>
          {bulletPoints.map((point, index) => (
            <ListItem
              key={index}
              disablePadding
              sx={{
                listStylePosition: "outside",
              }}
            >
              <ListItemIcon sx={{ minWidth: "32px" }}>
                <CheckCircleIcon sx={{ color: theme.palette.grey[200] }} />
              </ListItemIcon>
              <ListItemText
                primary={formatTextWithHighlight(
                  point,
                  theme.palette.secondary.main
                )}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Timeline */}
      <Typography
        variant="h4"
        align="left"
        gutterBottom
        sx={{ fontWeight: 700, mb: { xs: 3, md: 4 } }}
      >
        Career Roadmap:
      </Typography>
      <Box display="flex">
        <Box sx={{ position: "relative" }}>
          {/* Vertical line */}
          <Box
            sx={{
              position: "absolute",
              ml: 5.5,
              justifyContent: "center",
              top: 0,
              bottom: 0,
              width: "1px",
              backgroundColor: theme.palette.grey[200],
              mb: { xs: 9, sm: 8, lg: 8 },
              mt: { xs: 7, sm: 5, lg: 6 },
            }}
          />
          {timeline.map((item: TimelineItem, index: number) => (
            <Stack
              direction="row"
              spacing={2}
              alignItems="flex-start"
              key={index}
              sx={{
                mb: spacing,
                position: "relative",
                alignItems: "center",
              }}
            >
              {/* Time badge */}
              <Box
                sx={{
                  minWidth: "90px",
                  padding: theme.spacing(0.5, 1),
                  backgroundColor: theme.palette.grey[200],
                  borderRadius: "15px",
                  fontSize: "0.90rem",
                  fontWeight: 600,
                  textAlign: "center",
                  zIndex: 1,
                }}
              >
                {" "}
                {item.label}
              </Box>
              {/* Timeline content */}
              <Paper
                elevation={1}
                sx={{
                  flexGrow: 1,
                  padding: theme.spacing(2),
                  borderRadius: "10px",
                  width: "100%",
                  border: `1px solid ${theme.palette.text.disabled}`,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {item.title}
                </Typography>
                <Typography variant="body1">{item.description}</Typography>
              </Paper>
            </Stack>
          ))}
        </Box>
      </Box>
      {/* Final line */}
      <Typography variant="body1" maxWidth="1050px">
        <Box component="span" sx={{ fontWeight: 700 }}>
          {closingRemark.bold}
        </Box>{" "}
        {closingRemark.text}
      </Typography>
    </Box>
  );
};

export default CareerPathTimeline;
